import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
    hasInternalAdminPrivileges,
    type ActiveRoleAssignmentResponse,
    PaymentRequestStatus,
    type PaymentRequestDetailsView,
} from '@pillar-portal/api-types';
import {
    AuthorizableAction,
    ResourceType,
} from '@pillar-portal/database/dist/authorizable-actions';
import { ApiError, authApi, authorizationApi, paymentsApi } from '@/lib/pillarApi';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PaymentRequestDetailView } from '../PaymentRequestDetailView';
import {
    appendMerchantOrganizationUuid,
    getMerchantOrganizationUuidFromSearchParams,
} from '../merchant-organization-search';

const revocablePaymentRequestStatuses: ReadonlySet<string> = new Set([
    PaymentRequestStatus.SENT,
    PaymentRequestStatus.VIEWED,
    PaymentRequestStatus.PARTIALLY_PAID,
    PaymentRequestStatus.REISSUED,
]);

function getRevokeDisabledReason(params: {
    isRevoking: boolean;
    isAuthorized: boolean | null | undefined;
}): string | null {
    if (params.isRevoking) {
        return 'Revoke in progress.';
    }
    if (params.isAuthorized == null) {
        return 'Checking permissions.';
    }
    if (params.isAuthorized !== true) {
        return 'This functionality is disabled because you have not been granted the appropriate permission.';
    }
    return null;
}

function isRevocablePaymentRequestStatus(status: string): boolean {
    return revocablePaymentRequestStatuses.has(status);
}

export function MerchantPaymentRequestDetailContent() {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState<boolean>(false);
    const paymentRequestUuid = params.uuid;
    const merchantOrganizationUuid: string | undefined = getMerchantOrganizationUuidFromSearchParams(
        searchParams
    );

    if (paymentRequestUuid === undefined || paymentRequestUuid.trim().length === 0) {
        throw new Error('Missing payment request UUID');
    }

    const paymentRequestQuery = useQuery<PaymentRequestDetailsView>({
        queryKey: [
            'merchantPaymentRequestDetails',
            paymentRequestUuid,
            merchantOrganizationUuid ?? 'active-role',
        ],
        queryFn: async () =>
            merchantOrganizationUuid === undefined
                ? await paymentsApi.getMerchantPaymentRequestDetails(paymentRequestUuid)
                : await paymentsApi.getMerchantPaymentRequestDetails(paymentRequestUuid, {
                      merchant_organization_uuid: merchantOrganizationUuid,
                  }),
        retry: false,
    });
    const queryClient = useQueryClient();
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });
    const revokeAuthorizationQuery = useQuery({
        queryKey: ['paymentRequestDeleteAuthorization', paymentRequestUuid],
        queryFn: async () =>
            await authorizationApi.isAuthorized({
                action: AuthorizableAction.DELETE,
                resource_type: ResourceType.PAYMENT_REQUEST,
                target_resource: {
                    designator_type: ResourceType.PAYMENT_REQUEST,
                    payment_request_uuid: paymentRequestUuid,
                },
            }),
        staleTime: 30000,
    });
    const revokeMutation = useMutation({
        mutationFn: async () =>
            merchantOrganizationUuid === undefined
                ? await paymentsApi.revokePaymentRequestLink(paymentRequestUuid)
                : await paymentsApi.revokePaymentRequestLink(paymentRequestUuid, {
                      merchant_organization_uuid: merchantOrganizationUuid,
                  }),
        onSuccess: async () => {
            setIsRevokeConfirmOpen(false);
            await queryClient.invalidateQueries({
                queryKey: [
                    'merchantPaymentRequestDetails',
                    paymentRequestUuid,
                    merchantOrganizationUuid ?? 'active-role',
                ],
            });
        },
    });

    if (paymentRequestQuery.isLoading) {
        return <div>Loading payment request details...</div>;
    }

    if (paymentRequestQuery.error !== null || paymentRequestQuery.data === undefined) {
        const errorMessage =
            paymentRequestQuery.error instanceof ApiError
                ? paymentRequestQuery.error.message
                : 'Failed to load payment request details.';

        return (
            <Alert variant="destructive">
                <AlertTitle>Unable to load payment request</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    const revokeDisabledReason: string | null = getRevokeDisabledReason({
        isRevoking: revokeMutation.isPending,
        isAuthorized: revokeAuthorizationQuery.data?.is_authorized,
    });
    const canDisplayRevokeAction =
        isRevocablePaymentRequestStatus(paymentRequestQuery.data.status);
    const revokeAction = canDisplayRevokeAction ? (
        <div title={revokeDisabledReason ?? undefined} className="inline-block">
            <Button
                variant="destructive"
                disabled={revokeDisabledReason !== null}
                onClick={() => {
                    setIsRevokeConfirmOpen(true);
                }}
            >
                {revokeMutation.isPending ? 'Revoking...' : 'Revoke E-Invoice'}
            </Button>
        </div>
    ) : undefined;

    return (
        <>
            <PaymentRequestDetailView
                paymentRequest={paymentRequestQuery.data}
                pageDescription="Merchant payment-request details"
                backHref={appendMerchantOrganizationUuid(
                    '/payment-requests',
                    merchantOrganizationUuid
                )}
                backTooltip="Back to Payment Requests"
                actions={revokeAction}
                showPaymentTransactions
                showSupportDetails={hasInternalAdminPrivileges(
                    activeRoleAssignmentQuery.data ?? null
                )}
            />
            <ConfirmationModal
                open={isRevokeConfirmOpen}
                onOpenChange={setIsRevokeConfirmOpen}
                title="Revoke E-Invoice"
                message="This disables the current payment link."
                confirmLabel="Revoke"
                isLoading={revokeMutation.isPending}
                onConfirm={() => {
                    revokeMutation.mutate();
                }}
            />
        </>
    );
}
